import { useEffect, useState } from "react";
import { WifiOff, Download, X, CloudUpload, Loader2, Check, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { countPending, listPending, onQueueChange, syncAll } from "@/lib/offlineQueue";
import { countOfflineImages } from "@/lib/offlineStore";
import { api } from "@/lib/api";

/**
 * Beem — PWA + offline queue indicator.
 *  - Registra service worker.
 *  - Muestra badge cuando no hay red.
 *  - Muestra card flotante cuando hay pedidos en cola con botón "Sincronizar ahora".
 *  - Prompt de instalación.
 */
export default function OfflineIndicator() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [fotosOffline, setFotosOffline] = useState(1);          // >0 = tiene fotos
  const [showNoFotos, setShowNoFotos] = useState(false);        // banner dismissible

  const refresh = async () => {
    setPending(await countPending());
    setItems(await listPending());
  };

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/service-worker.js").then((reg) => {
          // Feb/2026: detectar SW nuevo y forzar recarga para tomar el bundle actualizado.
          // Sin esto, tras un deploy los usuarios siguen viendo la versión vieja hasta
          // que hacen Ctrl+Shift+R manualmente.
          if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
          reg.addEventListener("updatefound", () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener("statechange", () => {
              if (nw.state === "installed" && navigator.serviceWorker.controller) {
                nw.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });
          // Oct/2026: poll de actualizaciones cada 60s para pestañas que
          // llevan días abiertas (desktop vendedor). Sin esto el usuario
          // ve bugs ya arreglados hasta hacer Ctrl+Shift+R manual.
          const updateInterval = setInterval(() => {
            reg.update().catch(() => {});
          }, 60000);
          // También chequear al volver de background (visibilitychange)
          document.addEventListener("visibilitychange", () => {
            if (!document.hidden) reg.update().catch(() => {});
          });
          return () => clearInterval(updateInterval);
        }).catch(() => {});
        let reloaded = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (reloaded) return;
          reloaded = true;
          toast.info("Nueva versión disponible, recargando…", { duration: 2000 });
          setTimeout(() => window.location.reload(), 500);
        });
      });
    }
    refresh();
    const off = onQueueChange(refresh);
    const onOnline = async () => {
      setOnline(true);
      toast.success("Conexión restablecida");
      // Auto-sync will fire from api.js listener too — refresh state after
      setTimeout(refresh, 3500);
    };
    const onOffline = async () => {
      setOnline(false);
      toast.warning("Sin conexión. Los pedidos se guardarán localmente.");
      const n = await countOfflineImages();
      setFotosOffline(n);
      if (n === 0) setShowNoFotos(true);
    };
    // Al montar: si ya está offline verificamos también
    (async () => {
      const n = await countOfflineImages();
      setFotosOffline(n);
      if (!navigator.onLine && n === 0) setShowNoFotos(true);
    })();
    const onInstallable = (e) => { e.preventDefault(); setInstallPrompt(e); setShowInstall(true); };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("beforeinstallprompt", onInstallable);
    return () => {
      off?.();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("beforeinstallprompt", onInstallable);
    };
  }, []);

  const sync = async () => {
    if (!navigator.onLine) { toast.warning("Sin conexión — reintenta cuando vuelva la red"); return; }
    setSyncing(true);
    try {
      const res = await syncAll(api);
      if (res.ok > 0) toast.success(`Sincronizados ${res.ok} pedido${res.ok > 1 ? "s" : ""}`);
      if (res.failed > 0) toast.error(`${res.failed} pedido${res.failed > 1 ? "s" : ""} no se pudieron sincronizar`);
      if (res.ok === 0 && res.failed === 0) toast.info("Nada por sincronizar");
    } finally { setSyncing(false); await refresh(); }
  };

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setShowInstall(false);
  };

  return (
    <>
      {/* Offline badge */}
      {!online && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] bg-amber-500/95 text-white px-4 py-2 rounded-full text-xs font-semibold shadow-soft-lift flex items-center gap-2 backdrop-blur" data-testid="offline-indicator">
          <WifiOff className="w-3.5 h-3.5" />
          Modo offline · los pedidos se guardan en su dispositivo
        </div>
      )}

      {/* Banner: sin fotos offline — solo cuando está sin señal y no descargó fotos */}
      {!online && showNoFotos && fotosOffline === 0 && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[71] bg-rose-500/95 text-white px-4 py-2.5 rounded-lg text-xs font-semibold shadow-soft-lift flex items-center gap-3 backdrop-blur max-w-md" data-testid="no-fotos-offline-banner">
          <ImageOff className="w-4 h-4 shrink-0" />
          <div className="flex-1 min-w-0">
            <div>Sin fotos offline</div>
            <div className="text-[11px] font-normal opacity-90 mt-0.5">Prepara la visita cuando tengas señal para verlas sin datos.</div>
          </div>
          <button onClick={() => setShowNoFotos(false)} className="p-1 hover:bg-white/20 rounded" data-testid="no-fotos-banner-dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Pending queue floating card (bottom left) */}
      {pending > 0 && (
        <div className="fixed bottom-4 left-4 z-[70] w-[320px] bg-card border border-border rounded-2xl shadow-soft-lift overflow-hidden" data-testid="offline-queue">
          <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 px-4 py-3 hover:bg-accent/40">
            <div className="relative">
              <CloudUpload className={`w-5 h-5 ${online ? "text-primary" : "text-amber-500"}`} />
              <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                {pending}
              </span>
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-semibold text-foreground">
                {pending} pedido{pending > 1 ? "s" : ""} por sincronizar
              </div>
              <div className="text-[11px] text-muted-foreground">
                {online ? "Con conexión — listo para subir" : "Sin conexión"}
              </div>
            </div>
          </button>
          {expanded && (
            <div className="px-4 pb-3 border-t border-border/60 max-h-64 overflow-y-auto">
              <div className="pt-2 space-y-1.5">
                {items.map((it) => (
                  <div key={it.localId} className="flex items-center gap-2 py-1.5" data-testid={`queue-item-${it.localId}`}>
                    {it.status === "syncing" ? <Loader2 className="w-3 h-3 animate-spin text-primary" />
                      : it.status === "error" ? <X className="w-3 h-3 text-rose-500" />
                      : <div className="w-2 h-2 rounded-full bg-amber-500" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-foreground truncate">{it.snapshot?.cliente_nombre || "Cliente"}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {(it.snapshot?.items || []).length} productos
                        {it.lastError && <span className="text-rose-500"> · {String(typeof it.lastError === "string" ? it.lastError : JSON.stringify(it.lastError)).slice(0, 40)}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={sync} disabled={syncing || !online}
                className="w-full mt-3 h-9 rounded-full bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5 btn-tactile"
                data-testid="sync-now-btn"
              >
                {syncing ? <><Loader2 className="w-3 h-3 animate-spin" /> Sincronizando...</> : <><CloudUpload className="w-3 h-3" /> Sincronizar ahora</>}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Install PWA prompt */}
      {showInstall && installPrompt && (
        <div className="fixed bottom-4 right-4 z-[70] bg-card border border-border rounded-2xl shadow-soft-lift p-4 max-w-xs animate-slide-up" data-testid="install-prompt">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden ring-1 ring-primary/25">
              <img src="/beem-logo.jpg" alt="Beem" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground font-display italic">Instalar Beem</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Instálala en su celular para vender sin abrir el navegador — funciona sin señal.</div>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={install} className="text-[11px] bg-primary text-primary-foreground px-3 py-1.5 rounded-full font-semibold hover:bg-primary/90 flex items-center gap-1" data-testid="install-btn">
                  <Download className="w-3 h-3" /> Instalar
                </button>
                <button onClick={() => setShowInstall(false)} className="p-1 rounded-full hover:bg-accent text-muted-foreground">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
